import { c as _c } from "react/compiler-runtime";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { activitiesApi } from '../api';
import type { Activity, ActivityMutationResponse, DeleteMutationResponse } from '../api/types';
import { useObjectActivities, objectActivitiesQueryKey } from './useObjectActivities';
import { getReactionMode } from '../config/reactions';

// Hook to fetch user's reactions for an object
export function useUserReactions(actorId, objectId, objectType, options) {
  const $ = _c(7);
  const {
    data: t0
  } = useObjectActivities(objectId, objectType, options);
  let t1;
  if ($[0] !== actorId || $[1] !== t0) {
    const activities = t0 === undefined ? [] : t0;
    let t2;
    if ($[3] !== actorId) {
      t2 = activity => activity.type === "reaction" && String(activity.actorId) === actorId && activity.deletedAt === null;
      $[3] = actorId;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = activities.filter(t2).map(_temp);
    $[0] = actorId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const userReactions = t1;
  let t2;
  if ($[5] !== userReactions) {
    t2 = {
      data: userReactions
    };
    $[5] = userReactions;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}

// Hook to add a reaction
function _temp(activity_0) {
  return (activity_0.data as any)?.reaction_type || "";
}
export function useAddReaction() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp2,
      onSuccess: (response_0, variables) => {
        const queryKey = objectActivitiesQueryKey(variables.objectId, variables.objectType);
        const reactionMode = getReactionMode(variables.objectType);
        const actorId = response_0.activity.actorId;
        queryClient.setQueryData(queryKey, t1 => {
          const old = t1 === undefined ? [] : t1;
          if (reactionMode === "single") {
            const filtered = old.filter(activity => {
              if (activity.type !== "reaction") {
                return true;
              }
              if (String(activity.actorId) !== String(actorId)) {
                return true;
              }
              return false;
            });
            return [...filtered, response_0.activity];
          } else {
            return [...old, response_0.activity];
          }
        });
        if (response_0.engagement) {
          queryClient.setQueryData(["activities", "engagement", variables.objectType, variables.objectId], response_0.engagement);
        }
        if (variables.objectType === "item") {
          queryClient.setQueriesData({
            queryKey: ["items"]
          }, old_0 => {
            if (!old_0) {
              return old_0;
            }
            if (old_0.pages) {
              return {
                ...old_0,
                pages: old_0.pages.map(page => ({
                  ...page,
                  items: page.items.map(item => {
                    if (String(item.id) !== variables.objectId) {
                      return item;
                    }
                    const updatedItem = {
                      ...item
                    };
                    if (response_0.engagement) {
                      updatedItem.engagement = response_0.engagement.engagement;
                    }
                    const currentReactions = item.userReactions || [];
                    if (reactionMode === "single") {
                      updatedItem.userReactions = [{
                        id: response_0.activity.id,
                        type: "reaction",
                        reactionType: variables.reactionType,
                        createdAt: response_0.activity.createdAt
                      }];
                    } else {
                      updatedItem.userReactions = [...currentReactions, {
                        id: response_0.activity.id,
                        type: "reaction",
                        reactionType: variables.reactionType,
                        createdAt: response_0.activity.createdAt
                      }];
                    }
                    return updatedItem;
                  })
                }))
              };
            }
            if (old_0.items) {
              return {
                ...old_0,
                items: old_0.items.map(item_0 => {
                  if (String(item_0.id) !== variables.objectId) {
                    return item_0;
                  }
                  const updatedItem_0 = {
                    ...item_0
                  };
                  if (response_0.engagement) {
                    updatedItem_0.engagement = response_0.engagement.engagement;
                  }
                  const currentReactions_0 = item_0.userReactions || [];
                  if (reactionMode === "single") {
                    updatedItem_0.userReactions = [{
                      id: response_0.activity.id,
                      type: "reaction",
                      reactionType: variables.reactionType,
                      createdAt: response_0.activity.createdAt
                    }];
                  } else {
                    updatedItem_0.userReactions = [...currentReactions_0, {
                      id: response_0.activity.id,
                      type: "reaction",
                      reactionType: variables.reactionType,
                      createdAt: response_0.activity.createdAt
                    }];
                  }
                  return updatedItem_0;
                })
              };
            }
            return old_0;
          });
          queryClient.setQueriesData({
            queryKey: ["collections"]
          }, old_1 => {
            if (!old_1) {
              return old_1;
            }
            if (old_1.id && old_1.items) {
              return {
                ...old_1,
                items: old_1.items.map(item_1 => {
                  if (String(item_1.id) !== variables.objectId) {
                    return item_1;
                  }
                  const updatedItem_1 = {
                    ...item_1
                  };
                  if (response_0.engagement) {
                    updatedItem_1.engagement = response_0.engagement.engagement;
                  }
                  const currentReactions_1 = item_1.userReactions || [];
                  if (reactionMode === "single") {
                    updatedItem_1.userReactions = [{
                      id: response_0.activity.id,
                      type: "reaction",
                      reactionType: variables.reactionType,
                      createdAt: response_0.activity.createdAt
                    }];
                  } else {
                    updatedItem_1.userReactions = [...currentReactions_1, {
                      id: response_0.activity.id,
                      type: "reaction",
                      reactionType: variables.reactionType,
                      createdAt: response_0.activity.createdAt
                    }];
                  }
                  return updatedItem_1;
                })
              };
            }
            return old_1;
          });
          queryClient.setQueriesData({
            queryKey: ["quiz-question-analytics"]
          }, old_2 => {
            if (!old_2 || !old_2.analytics) {
              return old_2;
            }
            const itemKey = `item_${variables.objectId}`;
            const hasItem = old_2.analytics.some(entry => entry.items && entry.items[itemKey]);
            if (!hasItem) {
              return old_2;
            }
            return {
              ...old_2,
              analytics: old_2.analytics.map(entry_0 => {
                if (!entry_0.items || !entry_0.items[itemKey]) {
                  return entry_0;
                }
                const item_2 = entry_0.items[itemKey];
                const updatedItem_2 = {
                  ...item_2
                };
                if (response_0.engagement) {
                  updatedItem_2.engagement = response_0.engagement.engagement;
                }
                const currentReactions_2 = item_2.userReactions || [];
                if (reactionMode === "single") {
                  updatedItem_2.userReactions = [{
                    id: response_0.activity.id,
                    type: "reaction",
                    reactionType: variables.reactionType,
                    createdAt: response_0.activity.createdAt
                  }];
                } else {
                  updatedItem_2.userReactions = [...currentReactions_2, {
                    id: response_0.activity.id,
                    type: "reaction",
                    reactionType: variables.reactionType,
                    createdAt: response_0.activity.createdAt
                  }];
                }
                return {
                  ...entry_0,
                  items: {
                    ...entry_0.items,
                    [itemKey]: updatedItem_2
                  }
                };
              })
            };
          });
          queryClient.setQueryData(["items", variables.objectId], old_3 => {
            if (!old_3) {
              return old_3;
            }
            const updatedItem_3 = {
              ...old_3
            };
            if (response_0.engagement) {
              updatedItem_3.engagement = response_0.engagement.engagement;
            }
            const currentReactions_3 = old_3.userReactions || [];
            if (reactionMode === "single") {
              updatedItem_3.userReactions = [{
                id: response_0.activity.id,
                type: "reaction",
                reactionType: variables.reactionType,
                createdAt: response_0.activity.createdAt
              }];
            } else {
              updatedItem_3.userReactions = [...currentReactions_3, {
                id: response_0.activity.id,
                type: "reaction",
                reactionType: variables.reactionType,
                createdAt: response_0.activity.createdAt
              }];
            }
            return updatedItem_3;
          });
        } else {
          if (variables.objectType === "entity") {
            queryClient.setQueriesData({
              queryKey: ["entities"]
            }, old_4 => {
              if (!old_4) {
                return old_4;
              }
              if (old_4.pages) {
                return {
                  ...old_4,
                  pages: old_4.pages.map(page_0 => ({
                    ...page_0,
                    entities: page_0.entities.map(entity => {
                      if (String(entity.id) !== variables.objectId) {
                        return entity;
                      }
                      const updatedEntity = {
                        ...entity
                      };
                      if (response_0.engagement) {
                        updatedEntity.engagement = response_0.engagement.engagement;
                      }
                      const currentReactions_4 = entity.userReactions || [];
                      if (reactionMode === "single") {
                        updatedEntity.userReactions = [{
                          id: response_0.activity.id,
                          type: "reaction",
                          reactionType: variables.reactionType,
                          createdAt: response_0.activity.createdAt
                        }];
                      } else {
                        updatedEntity.userReactions = [...currentReactions_4, {
                          id: response_0.activity.id,
                          type: "reaction",
                          reactionType: variables.reactionType,
                          createdAt: response_0.activity.createdAt
                        }];
                      }
                      return updatedEntity;
                    })
                  }))
                };
              }
              if (old_4.entities) {
                return {
                  ...old_4,
                  entities: old_4.entities.map(entity_0 => {
                    if (String(entity_0.id) !== variables.objectId) {
                      return entity_0;
                    }
                    const updatedEntity_0 = {
                      ...entity_0
                    };
                    if (response_0.engagement) {
                      updatedEntity_0.engagement = response_0.engagement.engagement;
                    }
                    const currentReactions_5 = entity_0.userReactions || [];
                    if (reactionMode === "single") {
                      updatedEntity_0.userReactions = [{
                        id: response_0.activity.id,
                        type: "reaction",
                        reactionType: variables.reactionType,
                        createdAt: response_0.activity.createdAt
                      }];
                    } else {
                      updatedEntity_0.userReactions = [...currentReactions_5, {
                        id: response_0.activity.id,
                        type: "reaction",
                        reactionType: variables.reactionType,
                        createdAt: response_0.activity.createdAt
                      }];
                    }
                    return updatedEntity_0;
                  })
                };
              }
              return old_4;
            });
            queryClient.setQueryData(["entities", variables.objectId], old_5 => {
              if (!old_5) {
                return old_5;
              }
              const updatedEntity_1 = {
                ...old_5
              };
              if (response_0.engagement) {
                updatedEntity_1.engagement = response_0.engagement.engagement;
              }
              const currentReactions_6 = old_5.userReactions || [];
              if (reactionMode === "single") {
                updatedEntity_1.userReactions = [{
                  id: response_0.activity.id,
                  type: "reaction",
                  reactionType: variables.reactionType,
                  createdAt: response_0.activity.createdAt
                }];
              } else {
                updatedEntity_1.userReactions = [...currentReactions_6, {
                  id: response_0.activity.id,
                  type: "reaction",
                  reactionType: variables.reactionType,
                  createdAt: response_0.activity.createdAt
                }];
              }
              return updatedEntity_1;
            });
          }
        }
        queryClient.setQueriesData({
          queryKey: ["search"]
        }, old_6 => {
          if (!old_6 || !old_6.results) {
            return old_6;
          }
          return {
            ...old_6,
            results: old_6.results.map(result => {
              if (String(result.objectId) !== variables.objectId || result.objectType !== variables.objectType) {
                return result;
              }
              const updatedResult = {
                ...result
              };
              if (response_0.engagement) {
                updatedResult.engagement = response_0.engagement.engagement;
              }
              const currentReactions_7 = result.userReactions || [];
              if (reactionMode === "single") {
                updatedResult.userReactions = [{
                  id: response_0.activity.id,
                  type: "reaction",
                  reactionType: variables.reactionType,
                  createdAt: response_0.activity.createdAt
                }];
              } else {
                updatedResult.userReactions = [...currentReactions_7, {
                  id: response_0.activity.id,
                  type: "reaction",
                  reactionType: variables.reactionType,
                  createdAt: response_0.activity.createdAt
                }];
              }
              return updatedResult;
            })
          };
        });
        if (variables.objectType === "submission") {
          queryClient.setQueriesData({
            queryKey: ["quizzes"]
          }, old_7 => {
            if (!old_7) {
              return old_7;
            }
            if (old_7.pages) {
              return {
                ...old_7,
                pages: old_7.pages.map(page_1 => {
                  if (!page_1.submissions) {
                    return page_1;
                  }
                  return {
                    ...page_1,
                    submissions: page_1.submissions.map(submission => {
                      if (String(submission.id) !== variables.objectId) {
                        return submission;
                      }
                      const updatedSubmission = {
                        ...submission
                      };
                      if (response_0.engagement) {
                        updatedSubmission.engagement = response_0.engagement.engagement;
                      }
                      const currentReactions_8 = submission.userReactions || [];
                      if (reactionMode === "single") {
                        updatedSubmission.userReactions = [{
                          id: response_0.activity.id,
                          type: "reaction",
                          reactionType: variables.reactionType,
                          createdAt: response_0.activity.createdAt
                        }];
                      } else {
                        updatedSubmission.userReactions = [...currentReactions_8, {
                          id: response_0.activity.id,
                          type: "reaction",
                          reactionType: variables.reactionType,
                          createdAt: response_0.activity.createdAt
                        }];
                      }
                      return updatedSubmission;
                    })
                  };
                })
              };
            }
            return old_7;
          });
          queryClient.setQueriesData({
            queryKey: ["quiz-submissions"]
          }, old_8 => {
            if (!old_8) {
              return old_8;
            }
            if (String(old_8.id) !== variables.objectId) {
              return old_8;
            }
            const updatedSubmission_0 = {
              ...old_8
            };
            if (response_0.engagement) {
              updatedSubmission_0.engagement = response_0.engagement.engagement;
            }
            const currentReactions_9 = old_8.userReactions || [];
            if (reactionMode === "single") {
              updatedSubmission_0.userReactions = [{
                id: response_0.activity.id,
                type: "reaction",
                reactionType: variables.reactionType,
                createdAt: response_0.activity.createdAt
              }];
            } else {
              updatedSubmission_0.userReactions = [...currentReactions_9, {
                id: response_0.activity.id,
                type: "reaction",
                reactionType: variables.reactionType,
                createdAt: response_0.activity.createdAt
              }];
            }
            return updatedSubmission_0;
          });
        }
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}

// Hook to remove a reaction
async function _temp2(data) {
  const response = await activitiesApi.addReaction({
    ...data,
    includeEngagement: true
  });
  if ("activity" in response) {
    return response as ActivityMutationResponse;
  }
  return {
    activity: response as Activity
  };
}
export function useRemoveReaction() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp3,
      onSuccess: result => {
        const queryKey = objectActivitiesQueryKey(result.objectId, result.objectType);
        queryClient.setQueryData(queryKey, t1 => {
          const old = t1 === undefined ? [] : t1;
          return old.filter(activity => {
            if (activity.type !== "reaction") {
              return true;
            }
            if (String(activity.actorId) !== result.actorId) {
              return true;
            }
            if (result.reactionType) {
              return (activity.data as any)?.reaction_type !== result.reactionType;
            }
            return false;
          });
        });
        if (result.engagement) {
          queryClient.setQueryData(["activities", "engagement", result.objectType, result.objectId], result.engagement);
        }
        if (result.objectType === "item") {
          queryClient.setQueriesData({
            queryKey: ["items"]
          }, old_0 => {
            if (!old_0) {
              return old_0;
            }
            if (old_0.pages) {
              return {
                ...old_0,
                pages: old_0.pages.map(page => ({
                  ...page,
                  items: page.items.map(item => {
                    if (String(item.id) !== result.objectId) {
                      return item;
                    }
                    const updatedItem = {
                      ...item
                    };
                    if (result.engagement) {
                      updatedItem.engagement = result.engagement.engagement;
                    }
                    const currentReactions = item.userReactions || [];
                    updatedItem.userReactions = currentReactions.filter(r => {
                      if (result.reactionType) {
                        return r.reactionType !== result.reactionType;
                      }
                      return false;
                    });
                    return updatedItem;
                  })
                }))
              };
            }
            if (old_0.items) {
              return {
                ...old_0,
                items: old_0.items.map(item_0 => {
                  if (String(item_0.id) !== result.objectId) {
                    return item_0;
                  }
                  const updatedItem_0 = {
                    ...item_0
                  };
                  if (result.engagement) {
                    updatedItem_0.engagement = result.engagement.engagement;
                  }
                  const currentReactions_0 = item_0.userReactions || [];
                  updatedItem_0.userReactions = currentReactions_0.filter(r_0 => {
                    if (result.reactionType) {
                      return r_0.reactionType !== result.reactionType;
                    }
                    return false;
                  });
                  return updatedItem_0;
                })
              };
            }
            return old_0;
          });
          queryClient.setQueriesData({
            queryKey: ["collections"]
          }, old_1 => {
            if (!old_1) {
              return old_1;
            }
            if (old_1.id && old_1.items) {
              return {
                ...old_1,
                items: old_1.items.map(item_1 => {
                  if (String(item_1.id) !== result.objectId) {
                    return item_1;
                  }
                  const updatedItem_1 = {
                    ...item_1
                  };
                  if (result.engagement) {
                    updatedItem_1.engagement = result.engagement.engagement;
                  }
                  const currentReactions_1 = item_1.userReactions || [];
                  updatedItem_1.userReactions = currentReactions_1.filter(r_1 => {
                    if (result.reactionType) {
                      return r_1.reactionType !== result.reactionType;
                    }
                    return false;
                  });
                  return updatedItem_1;
                })
              };
            }
            return old_1;
          });
          queryClient.setQueriesData({
            queryKey: ["quiz-question-analytics"]
          }, old_2 => {
            if (!old_2 || !old_2.analytics) {
              return old_2;
            }
            const itemKey = `item_${result.objectId}`;
            const hasItem = old_2.analytics.some(entry => entry.items && entry.items[itemKey]);
            if (!hasItem) {
              return old_2;
            }
            return {
              ...old_2,
              analytics: old_2.analytics.map(entry_0 => {
                if (!entry_0.items || !entry_0.items[itemKey]) {
                  return entry_0;
                }
                const item_2 = entry_0.items[itemKey];
                const updatedItem_2 = {
                  ...item_2
                };
                if (result.engagement) {
                  updatedItem_2.engagement = result.engagement.engagement;
                }
                const currentReactions_2 = item_2.userReactions || [];
                updatedItem_2.userReactions = currentReactions_2.filter(r_2 => {
                  if (result.reactionType) {
                    return r_2.reactionType !== result.reactionType;
                  }
                  return false;
                });
                return {
                  ...entry_0,
                  items: {
                    ...entry_0.items,
                    [itemKey]: updatedItem_2
                  }
                };
              })
            };
          });
          queryClient.setQueryData(["items", result.objectId], old_3 => {
            if (!old_3) {
              return old_3;
            }
            const updatedItem_3 = {
              ...old_3
            };
            if (result.engagement) {
              updatedItem_3.engagement = result.engagement.engagement;
            }
            const currentReactions_3 = old_3.userReactions || [];
            updatedItem_3.userReactions = currentReactions_3.filter(r_3 => {
              if (result.reactionType) {
                return r_3.reactionType !== result.reactionType;
              }
              return false;
            });
            return updatedItem_3;
          });
        } else {
          if (result.objectType === "entity") {
            queryClient.setQueriesData({
              queryKey: ["entities"]
            }, old_4 => {
              if (!old_4) {
                return old_4;
              }
              if (old_4.pages) {
                return {
                  ...old_4,
                  pages: old_4.pages.map(page_0 => ({
                    ...page_0,
                    entities: page_0.entities.map(entity => {
                      if (String(entity.id) !== result.objectId) {
                        return entity;
                      }
                      const updatedEntity = {
                        ...entity
                      };
                      if (result.engagement) {
                        updatedEntity.engagement = result.engagement.engagement;
                      }
                      const currentReactions_4 = entity.userReactions || [];
                      updatedEntity.userReactions = currentReactions_4.filter(r_4 => {
                        if (result.reactionType) {
                          return r_4.reactionType !== result.reactionType;
                        }
                        return false;
                      });
                      return updatedEntity;
                    })
                  }))
                };
              }
              if (old_4.entities) {
                return {
                  ...old_4,
                  entities: old_4.entities.map(entity_0 => {
                    if (String(entity_0.id) !== result.objectId) {
                      return entity_0;
                    }
                    const updatedEntity_0 = {
                      ...entity_0
                    };
                    if (result.engagement) {
                      updatedEntity_0.engagement = result.engagement.engagement;
                    }
                    const currentReactions_5 = entity_0.userReactions || [];
                    updatedEntity_0.userReactions = currentReactions_5.filter(r_5 => {
                      if (result.reactionType) {
                        return r_5.reactionType !== result.reactionType;
                      }
                      return false;
                    });
                    return updatedEntity_0;
                  })
                };
              }
              return old_4;
            });
            queryClient.setQueryData(["entities", result.objectId], old_5 => {
              if (!old_5) {
                return old_5;
              }
              const updatedEntity_1 = {
                ...old_5
              };
              if (result.engagement) {
                updatedEntity_1.engagement = result.engagement.engagement;
              }
              const currentReactions_6 = old_5.userReactions || [];
              updatedEntity_1.userReactions = currentReactions_6.filter(r_6 => {
                if (result.reactionType) {
                  return r_6.reactionType !== result.reactionType;
                }
                return false;
              });
              return updatedEntity_1;
            });
          }
        }
        queryClient.setQueriesData({
          queryKey: ["search"]
        }, old_6 => {
          if (!old_6 || !old_6.results) {
            return old_6;
          }
          return {
            ...old_6,
            results: old_6.results.map(result_item => {
              if (String(result_item.objectId) !== result.objectId || result_item.objectType !== result.objectType) {
                return result_item;
              }
              const updatedResult = {
                ...result_item
              };
              if (result.engagement) {
                updatedResult.engagement = result.engagement.engagement;
              }
              const currentReactions_7 = result_item.userReactions || [];
              updatedResult.userReactions = currentReactions_7.filter(r_7 => {
                if (result.reactionType) {
                  return r_7.reactionType !== result.reactionType;
                }
                return false;
              });
              return updatedResult;
            })
          };
        });
        if (result.objectType === "submission") {
          queryClient.setQueriesData({
            queryKey: ["quizzes"]
          }, old_7 => {
            if (!old_7) {
              return old_7;
            }
            if (old_7.pages) {
              return {
                ...old_7,
                pages: old_7.pages.map(page_1 => {
                  if (!page_1.submissions) {
                    return page_1;
                  }
                  return {
                    ...page_1,
                    submissions: page_1.submissions.map(submission => {
                      if (String(submission.id) !== result.objectId) {
                        return submission;
                      }
                      const updatedSubmission = {
                        ...submission
                      };
                      if (result.engagement) {
                        updatedSubmission.engagement = result.engagement.engagement;
                      }
                      const currentReactions_8 = submission.userReactions || [];
                      updatedSubmission.userReactions = currentReactions_8.filter(r_8 => {
                        if (result.reactionType) {
                          return r_8.reactionType !== result.reactionType;
                        }
                        return false;
                      });
                      return updatedSubmission;
                    })
                  };
                })
              };
            }
            return old_7;
          });
          queryClient.setQueriesData({
            queryKey: ["quiz-submissions"]
          }, old_8 => {
            if (!old_8) {
              return old_8;
            }
            if (String(old_8.id) !== result.objectId) {
              return old_8;
            }
            const updatedSubmission_0 = {
              ...old_8
            };
            if (result.engagement) {
              updatedSubmission_0.engagement = result.engagement.engagement;
            }
            const currentReactions_9 = old_8.userReactions || [];
            updatedSubmission_0.userReactions = currentReactions_9.filter(r_9 => {
              if (result.reactionType) {
                return r_9.reactionType !== result.reactionType;
              }
              return false;
            });
            return updatedSubmission_0;
          });
        }
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}

// Hook to toggle a reaction (add if not present, remove if present)
async function _temp3(data) {
  const response = await activitiesApi.removeReaction(data.objectId, data.objectType, data.reactionType, true);
  const engagement = response && typeof response === "object" && "engagement" in response ? (response as DeleteMutationResponse).engagement : undefined;
  return {
    ...data,
    engagement
  };
}
export function useToggleReaction() {
  const $ = _c(8);
  const addReaction = useAddReaction();
  const removeReaction = useRemoveReaction();
  let t0;
  if ($[0] !== addReaction || $[1] !== removeReaction) {
    t0 = async data => {
      const hasReaction = data.currentReactions.includes(data.reactionType);
      if (hasReaction) {
        await removeReaction.mutateAsync({
          actorId: data.actorId,
          objectId: data.objectId,
          objectType: data.objectType,
          reactionType: data.reactionType
        });
      } else {
        await addReaction.mutateAsync({
          objectId: data.objectId,
          objectType: data.objectType,
          reactionType: data.reactionType
        });
      }
    };
    $[0] = addReaction;
    $[1] = removeReaction;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const t1 = addReaction.isPending || removeReaction.isPending;
  const t2 = addReaction.isError || removeReaction.isError;
  const t3 = addReaction.error || removeReaction.error;
  let t4;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      mutate: t0,
      isPending: t1,
      isError: t2,
      error: t3
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}